import React from 'react'
import { useNavigate } from 'react-router'
import styled from 'styled-components'
import { Button } from 'antd'
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons'
import theme from '../../theme'
import { useReturnStatus } from '../../hooks/useReturnStatus'

const StatusContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 10px;
`

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  line-height: 21px;
`

const PendingIcon = styled(ClockCircleOutlined)`
  color: ${theme.sunflower500};
  font-size: 16px;
`

const ApprovedIcon = styled(CheckCircleOutlined)`
  color: ${theme.cornFlower500};
  font-size: 16px;
`

const RejectedIcon = styled(CloseCircleOutlined)`
  color: ${theme.peach500};
  font-size: 16px;
`

const StatusText = styled.span`
  color: ${theme.midnight800};
  font-weight: 100;
`

const RejectionReason = styled.p`
  color: ${theme.slate500};
  font-size: 12px;
  line-height: 18px;
  margin: 4px 0 0 24px;
`

type Props = {
  returnRequestId?: string
  existingReturnTrackingUrl?: string
}

export default function ReturnStatusDisplay({
  returnRequestId,
  existingReturnTrackingUrl,
}: Props) {
  const navigate = useNavigate()
  const { data: status } = useReturnStatus(returnRequestId)

  // No requestId means we know a return exists but can't poll (e.g. after page refresh)
  if (!returnRequestId && !status) {
    return (
      <StatusContainer data-testid="return-status-display">
        <StatusRow>
          <PendingIcon />
          <StatusText>A return has been requested for this order.</StatusText>
        </StatusRow>
        {existingReturnTrackingUrl && (
          <Button
            type="link"
            onClick={() => navigate(existingReturnTrackingUrl)}
            style={{ padding: '0 0 0 24px', height: 'auto' }}
            data-testid="track-return-link"
          >
            Track Your Return
          </Button>
        )}
      </StatusContainer>
    )
  }

  if (!status) return null

  const isPendingReview = ['submitted', 'pending'].includes(status.status)
  const isBookingInProgress = status.status === 'approvedPendingBooking'

  return (
    <StatusContainer data-testid="return-status-display">
      {isPendingReview && (
        <StatusRow>
          <PendingIcon />
          <StatusText>
            Your return request has been submitted. The merchant will review it
            shortly.
          </StatusText>
        </StatusRow>
      )}

      {isBookingInProgress && (
        <StatusRow>
          <ApprovedIcon />
          <StatusText>
            Your return has been approved! We're arranging the pickup.
          </StatusText>
        </StatusRow>
      )}

      {status.status === 'approved' && (
        <>
          <StatusRow>
            <ApprovedIcon />
            <StatusText>Your return has been approved!</StatusText>
          </StatusRow>
          {status.returnTrackingUrl && (
            <Button
              type="link"
              href={status.returnTrackingUrl}
              target="_blank"
              style={{ padding: '0 0 0 24px', height: 'auto' }}
            >
              Track your return
            </Button>
          )}
        </>
      )}

      {status.status === 'rejected' && (
        <>
          <StatusRow>
            <RejectedIcon />
            <StatusText>Your return request was declined.</StatusText>
          </StatusRow>
          {status.rejectionReason && (
            <RejectionReason>
              Reason: {status.rejectionReason}
            </RejectionReason>
          )}
        </>
      )}
    </StatusContainer>
  )
}
